import React from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Settings,
  Map,
  BookOpen,
  Swords,
  Users,
  ShieldCheck,
  Trophy,
  Layers,
  X
} from 'lucide-react'
import type { User } from '@/types'
import { useIsAdmin, useIsCaptain } from '@/store/authStore'
import { useTerminology } from '@/hooks/useTerminology'
import { cn } from '@/utils'
import styles from './Sidebar.module.css'

interface SidebarProps {
  user: User | null
  isMobileMenuOpen?: boolean
  onCloseMobileMenu?: () => void
}

export function Sidebar({ user, isMobileMenuOpen = false, onCloseMobileMenu }: SidebarProps) {
  const location = useLocation()
  const isAdmin = useIsAdmin()
  const isCaptain = useIsCaptain()
  const t = useTerminology()

  const navigation = [
    {
      name: 'DASHBOARD',
      href: '/dashboard',
      icon: LayoutDashboard,
      description: 'Command center',
      minRole: 'pirate' as const,
      tourId: undefined as string | undefined,
    },
    {
      name: 'COURSE CATALOG',
      href: '/courses',
      icon: Map,
      description: 'Browse all available courses',
      minRole: 'pirate' as const,
      tourId: 'nav-courses',
    },
    {
      name: `MY ${t.course.upperPlural}`,
      href: '/my-courses',
      icon: BookOpen,
      description: `${t.course.titlePlural} you run`,
      minRole: 'captain' as const,
      tourId: 'nav-my-courses',
    },
    {
      name: t.lesson.upperPlural,
      href: '/lessons',
      icon: Swords,
      description: `${t.lesson.title} builder`,
      minRole: 'admin' as const,
      tourId: undefined as string | undefined,
    },
    {
      name: 'CARD GLOSSARY',
      href: '/cards',
      icon: Layers,
      description: 'Enter The Spudnet card repository',
      minRole: 'captain' as const,
      tourId: undefined as string | undefined,
    },
    {
      name: 'ORGANIZATIONS',
      href: '/organizations',
      icon: Users,
      description: 'Team management',
      minRole: 'admin' as const,
      tourId: undefined as string | undefined,
    },
    {
      name: 'USERS',
      href: '/admin/users',
      icon: ShieldCheck,
      description: 'User management',
      minRole: 'admin' as const,
      tourId: undefined as string | undefined,
    },
    {
      name: 'LEADERBOARD',
      href: '/leaderboard',
      icon: Trophy,
      description: 'Pirate rankings',
      minRole: 'pirate' as const,
      tourId: undefined as string | undefined,
    },
    {
      name: 'SETTINGS',
      href: '/settings',
      icon: Settings,
      description: 'System config',
      minRole: 'pirate' as const,
      tourId: undefined as string | undefined,
    },
  ]

  const filteredNavigation = navigation.filter(item => {
    if (item.minRole === 'admin') return isAdmin
    if (item.minRole === 'captain') return isCaptain
    return true
  })

  const handleNavClick = () => {
    // Close mobile menu when navigating
    if (onCloseMobileMenu) {
      onCloseMobileMenu()
    }
  }

  return (
    <div className={cn(
      styles.sidebar,
      isMobileMenuOpen && styles.sidebarOpen
    )}>
      {/* Logo */}
      <div className={styles.logoSection}>
        <NavLink to="/dashboard" onClick={handleNavClick} className={styles.logoWrapper}>
          <div className={styles.logoIcon}>
            <img src="/armadafavicon.png" alt="Potato Pirates" width={32} height={32} />
          </div>
          <div className={styles.logoText}>
            <h1>POTATO PIRATES</h1>
            <p>THE ARMADA</p>
          </div>
        </NavLink>

        {/* Mobile close button */}
        {isMobileMenuOpen && onCloseMobileMenu && (
          <button
            className={styles.mobileCloseBtn}
            onClick={onCloseMobileMenu}
            aria-label="Close mobile menu"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>


      {/* Navigation */}
      <nav className={styles.navigation} aria-label="Main navigation">
        {filteredNavigation.map((item) => {
          const isOrgCoursePage = location.pathname.startsWith('/organizations/') && location.pathname.includes('/courses/')
          const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/')
            || (item.href === '/my-courses' && isOrgCoursePage)
          return (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={handleNavClick}
              data-tour={item.tourId}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                styles.navLink,
                isActive && styles.navLinkActive
              )}
            >
              <item.icon className={styles.navIcon} />
              <div className={styles.navContent}>
                <p>{item.name}</p>
                <p>{item.description}</p>
              </div>
            </NavLink>
          )
        })}
      </nav>



    </div>
  )
}
